import { useCallback, useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { Box, Button, Chip, Stack, Typography } from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import BadgeScanner from '../components/BadgeScanner'
import { adminService } from '../../services/adminService'
import type { EventInfo } from '../../data/types'

type Banner = {
  severity: 'success' | 'info' | 'error'
  name: string
  detail?: string
}

const BANNER_COLORS = {
  success: { bg: '#1b5e20', icon: <CheckCircleRoundedIcon sx={{ fontSize: 48 }} /> },
  info: { bg: '#e65100', icon: <InfoRoundedIcon sx={{ fontSize: 48 }} /> },
  error: { bg: '#b71c1c', icon: <ErrorRoundedIcon sx={{ fontSize: 48 }} /> },
}

/**
 * Full-screen, chromeless kiosk check-in scanner for a dedicated device at the
 * door. Stays under RequireAdmin (the device must be signed in as staff, since
 * the scan endpoint is role-guarded). Large video + reticle, a running tally,
 * and a big result flash sized for arm's-length reading.
 */
export default function AdminKioskScanPage() {
  const { id: eventId } = useParams()
  const [event, setEvent] = useState<EventInfo | null>(null)
  const [checkedInCount, setCheckedInCount] = useState<number | null>(null)
  const [total, setTotal] = useState<number | null>(null)
  const [banner, setBanner] = useState<Banner | null>(null)
  const [paused, setPaused] = useState(false)
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const refreshSummary = useCallback(async () => {
    if (!eventId) return
    try {
      const s = await adminService.checkinSummary(eventId)
      setCheckedInCount(s.checkedIn)
      setTotal(s.total)
    } catch {
      /* leave last-known counts */
    }
  }, [eventId])

  useEffect(() => {
    if (!eventId) return
    adminService.getEvent(eventId).then(setEvent).catch(() => {})
    refreshSummary()
  }, [eventId, refreshSummary])

  useEffect(() => () => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current)
  }, [])

  const handleDecode = useCallback(
    async (token: string) => {
      if (!eventId) return
      setPaused(true)
      try {
        const res = await adminService.scanCheckin(eventId, token)
        const name = res.fullName || res.email
        setBanner(
          res.alreadyCheckedIn
            ? { severity: 'info', name, detail: 'Already checked in' }
            : { severity: 'success', name, detail: 'Checked in' },
        )
        if (!res.alreadyCheckedIn) refreshSummary()
      } catch {
        setBanner({ severity: 'error', name: 'Invalid code', detail: 'Not recognized for this event' })
      }
      if (resumeTimer.current) clearTimeout(resumeTimer.current)
      resumeTimer.current = setTimeout(() => {
        setPaused(false)
        setBanner(null)
      }, 2000)
    },
    [eventId, refreshSummary],
  )

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: '#0b1620',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        zIndex: (t) => t.zIndex.modal + 1,
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={800}>
            Check-in
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {event?.name ?? 'Event'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          {checkedInCount !== null && (
            <Chip
              icon={<CheckCircleRoundedIcon />}
              label={`${checkedInCount}${total !== null ? ` / ${total}` : ''} checked in`}
              color="success"
              sx={{ fontWeight: 700, fontSize: '1rem', py: 2.5, px: 1 }}
            />
          )}
          <Button
            component={RouterLink}
            to={eventId ? `/admin/events/${eventId}` : '/admin'}
            startIcon={<CloseRoundedIcon />}
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}
            variant="outlined"
          >
            Exit
          </Button>
        </Stack>
      </Stack>

      {/* Scanner */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, md: 6 },
          pb: 4,
          minHeight: 0,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 720 }}>
          <BadgeScanner onDecode={handleDecode} paused={paused} height={440} />
        </Box>
      </Box>

      {/* Big result flash */}
      {banner && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: BANNER_COLORS[banner.severity].bg,
            color: '#fff',
            px: 4,
            py: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {BANNER_COLORS[banner.severity].icon}
          <Box>
            <Typography variant="h5" fontWeight={800}>
              {banner.name}
            </Typography>
            {banner.detail && (
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                {banner.detail}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
